/**
 * Split validation. Ported from `BudgetStore+Remote.swift` and mirrored by
 * `aws-backend/src/lambda/domain/allocations.ts`.
 *
 * The server enforces these rules too. Validating client-side as well is not
 * redundancy for its own sake: it lets the UI disable the save button and say
 * what is wrong while the user is still typing, instead of after a round trip.
 */

import { formatMoney, roundCents, sumCents } from './money';

/** A cent of slack, so splitting $10.00 three ways is allowed to be $3.33/$3.33/$3.34. */
export const ALLOCATION_SUM_TOLERANCE = 0.01;

/** DynamoDB caps a `TransactWrite` at 25 items, and each split costs one. */
export const MAX_SPLITS = 25;

export interface SplitDraft {
  budgetItemId: string;
  /** Always positive. Direction comes from the transaction. */
  amount: number;
  notes?: string | null;
}

export type SplitProblem =
  | { code: 'tooFew'; message: string }
  | { code: 'tooMany'; message: string }
  | { code: 'missingBudgetItem'; message: string }
  | { code: 'nonPositiveAmount'; message: string }
  | { code: 'sumMismatch'; message: string; difference: number };

export interface SplitValidation {
  isValid: boolean;
  problems: SplitProblem[];
  total: number;
  /** Signed: positive when the splits add up to more than the transaction. */
  difference: number;
}

/**
 * Checks a set of splits against the transaction they divide.
 *
 * `transactionAmount` is the signed transaction amount; only its magnitude
 * matters, so a -$60 charge and a +$60 refund split identically.
 */
export function validateSplits(
  splits: readonly SplitDraft[],
  transactionAmount: number
): SplitValidation {
  const problems: SplitProblem[] = [];
  const target = Math.abs(roundCents(transactionAmount));
  const total = sumCents(splits.map((split) => Math.abs(split.amount)));
  const difference = roundCents(total - target);

  if (splits.length < 2) {
    problems.push({
      code: 'tooFew',
      message: 'A split needs at least two categories',
    });
  }
  if (splits.length > MAX_SPLITS) {
    problems.push({
      code: 'tooMany',
      message: `A transaction can be split across at most ${MAX_SPLITS} categories`,
    });
  }
  if (splits.some((split) => !split.budgetItemId)) {
    problems.push({
      code: 'missingBudgetItem',
      message: 'Every part needs a category',
    });
  }
  if (splits.some((split) => Math.abs(split.amount) <= 0)) {
    problems.push({
      code: 'nonPositiveAmount',
      message: 'Every part needs an amount greater than zero',
    });
  }
  if (Math.abs(difference) > ALLOCATION_SUM_TOLERANCE) {
    problems.push({
      code: 'sumMismatch',
      message:
        difference > 0
          ? `The parts add up to ${formatMoney(Math.abs(difference))} more than the transaction`
          : `The parts are ${formatMoney(Math.abs(difference))} short of the transaction`,
      difference,
    });
  }

  return { isValid: problems.length === 0, problems, total, difference };
}

/**
 * Splits an amount into `count` even parts, giving the leftover cents to the
 * first parts. $10.00 in three becomes $3.34, $3.33, $3.33 — which sums exactly.
 */
export function evenSplit(transactionAmount: number, count: number): number[] {
  if (count <= 0) return [];
  const totalCents = Math.round(Math.abs(transactionAmount) * 100);
  const base = Math.floor(totalCents / count);
  const remainder = totalCents - base * count;
  return Array.from({ length: count }, (_, index) =>
    roundCents((base + (index < remainder ? 1 : 0)) / 100)
  );
}
